"use client";

// The price chart on a laptop page: the cheapest new listing at each date we
// looked.
//
// It charts the lowest price across sellers rather than one seller's own line,
// because listings come and go — following a single one would end the line the
// week that seller stopped being scraped, which reads as "no longer sold".
//
// The heading says "we've tracked" deliberately. The big price above this
// section is the Amazon buy target, and the two can legitimately disagree: a
// cheaper seller may exist, or the last capture may be six days old. Claiming
// this line *is* the price would make one of them look broken.
import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from "react";
import { normCurrencySymbol } from "@/lib/amazon";
import type { PriceHistoryResponse, PricePoint } from "@/lib/types";
import { RANGES, areaPath, buildScales, formatDay, linePath, nearestIndex, sliceRange, type Range } from "@/lib/price-history";
const BOX = {
  width: 720,
  height: 240,
  padTop: 16,
  padRight: 14,
  padBottom: 30,
  padLeft: 52
};
const BASELINE_Y = BOX.height - BOX.padBottom;
function money(price: number, sym: string): string {
  return `${sym}${Math.round(price).toLocaleString("en-GB")}`;
}
export function PriceHistorySection(t0) {
  const $ = _c(30);
  const {
    history,
    loading,
    currentPrice
  } = t0;
  const [range, setRange] = useState("all");
  const [hovered, setHovered] = useState(null);
  let t1;
  if ($[0] !== history?.lowest_new) {
    t1 = history?.lowest_new ?? [];
    $[0] = history?.lowest_new;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const all = t1;
  const t2 = history?.currency ?? undefined;
  let t3;
  if ($[2] !== t2) {
    t3 = normCurrencySymbol(t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const sym = t3;
  let t4;
  if ($[4] !== all) {
    t4 = RANGES.filter(r => sliceRange(all, r.key).length >= 2).map(_temp);
    $[4] = all;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const available = t4;
  let t5;
  if ($[6] !== available || $[7] !== range) {
    t5 = available.includes(range) ? range : available[available.length - 1];
    $[6] = available;
    $[7] = range;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const activeRange = t5;
  let t6;
  if ($[9] !== activeRange || $[10] !== all) {
    t6 = activeRange ? sliceRange(all, activeRange) : [];
    $[9] = activeRange;
    $[10] = all;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const points = t6;
  if (!loading && !history) {
    return null;
  }
  if (loading) {
    let t7;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <section className="mt-8 rounded-2xl border border-border/60 bg-card p-5"><div className="h-4 w-32 animate-pulse rounded-full bg-foreground/8" /><div className="mt-4 h-44 animate-pulse rounded-xl bg-foreground/5" /></section>;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    return t7;
  }
  const thin = points.length < 2;
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <h2 className="text-sm font-semibold tracking-tight">Lowest price we've tracked</h2>;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== activeRange || $[15] !== available || $[16] !== thin) {
    t8 = !thin && available.length > 1 && <div role="group" aria-label="Time range" className="flex items-center gap-1.5">{RANGES.map(r_1 => {
        const enabled = available.includes(r_1.key);
        const active = r_1.key === activeRange;
        return <button key={r_1.key} type="button" disabled={!enabled} aria-pressed={active} onClick={() => {
          setRange(r_1.key);
          setHovered(null);
        }} className={`rounded-full px-3 py-1.5 text-[11px] font-medium ring-1 transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${active ? "bg-foreground text-background ring-foreground" : "bg-card text-foreground ring-border/60 hover:ring-foreground/30"}`}>{r_1.label}</button>;
      })}</div>;
    $[14] = activeRange;
    $[15] = available;
    $[16] = thin;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t8) {
    t9 = <div className="flex flex-wrap items-center justify-between gap-3">{t7}{t8}</div>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== currentPrice || $[21] !== history?.first_captured_at || $[22] !== hovered || $[23] !== points || $[24] !== sym || $[25] !== thin) {
    t10 = thin ? <ThinState currentPrice={currentPrice} sym={sym} since={history?.first_captured_at ?? null} /> : <Chart points={points} sym={sym} hovered={hovered} onHover={setHovered} />;
    $[20] = currentPrice;
    $[21] = history?.first_captured_at;
    $[22] = hovered;
    $[23] = points;
    $[24] = sym;
    $[25] = thin;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t9) {
    t11 = <section className="mt-8 rounded-2xl border border-border/60 bg-card p-5">{t9}{t10}</section>;
    $[27] = t10;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
}

/**
 * What the section shows before there is a line: the price now, and when we
 * started watching it. The table starts empty and the refresh runs twice a week,
 * so this is what every laptop shows for the first week or so.
 */
function _temp(r_0) {
  return r_0.key;
}
function ThinState(t0) {
  const $ = _c(10);
  const {
    currentPrice,
    sym,
    since
  } = t0;
  let t1;
  if ($[0] !== currentPrice || $[1] !== sym) {
    t1 = currentPrice != null && <p className="text-2xl font-bold tabular-nums tracking-tight">{money(currentPrice, sym)}</p>;
    $[0] = currentPrice;
    $[1] = sym;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== since) {
    t2 = since ? `We started tracking this price on ${formatDay(since, true)}. We check it twice a week, so the chart fills in from here.` : "We started tracking this price today. We check it twice a week, so the chart fills in from here.";
    $[3] = since;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{t2}</p>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t3) {
    t4 = <div className="mt-4">{t1}{t3}</div>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function Chart(t0) {
  const $ = _c(115);
  const {
    points,
    sym,
    hovered,
    onHover
  } = t0;
  let t1;
  if ($[0] !== points) {
    t1 = buildScales(points, BOX);
    $[0] = points;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const scales = t1;
  const last = points[points.length - 1];
  const active = hovered != null ? points[hovered] : null;
  let summary;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== active || $[3] !== last || $[4] !== onHover || $[5] !== points || $[6] !== scales || $[7] !== sym) {
    const low = points.reduce(_temp2, points[0]);
    let t11;
    if ($[18] !== points[0].d) {
      t11 = points[0].d.slice(0, 4);
      $[18] = points[0].d;
      $[19] = t11;
    } else {
      t11 = $[19];
    }
    let t12;
    if ($[20] !== last.d) {
      t12 = last.d.slice(0, 4);
      $[20] = last.d;
      $[21] = t12;
    } else {
      t12 = $[21];
    }
    const spansYears = t11 !== t12;
    let t13;
    if ($[22] !== points[0].p || $[23] !== sym) {
      t13 = money(points[0].p, sym);
      $[22] = points[0].p;
      $[23] = sym;
      $[24] = t13;
    } else {
      t13 = $[24];
    }
    let t14;
    if ($[25] !== points[0].d) {
      t14 = formatDay(points[0].d, true);
      $[25] = points[0].d;
      $[26] = t14;
    } else {
      t14 = $[26];
    }
    let t15;
    if ($[27] !== last.p || $[28] !== sym) {
      t15 = money(last.p, sym);
      $[27] = last.p;
      $[28] = sym;
      $[29] = t15;
    } else {
      t15 = $[29];
    }
    let t16;
    if ($[30] !== last.d) {
      t16 = formatDay(last.d, true);
      $[30] = last.d;
      $[31] = t16;
    } else {
      t16 = $[31];
    }
    summary = `Lowest tracked price, ${t13} on ${t14} ` + `to ${t15} on ${t16}. ` + `Cheapest was ${money(low.p, sym)} on ${formatDay(low.d, true)}.`;
    let t17;
    if ($[32] !== onHover) {
      t17 = () => onHover(null);
      $[32] = onHover;
      $[33] = t17;
    } else {
      t17 = $[33];
    }
    let t18;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <defs><linearGradient id="price-history-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="var(--brand)" stopOpacity="0.18" /><stop offset="100%" stopColor="var(--brand)" stopOpacity="0" /></linearGradient></defs>;
      $[34] = t18;
    } else {
      t18 = $[34];
    }
    let t19;
    if ($[35] !== scales || $[36] !== sym) {
      t19 = scales.yTicks.map(tick => <g key={tick}><line x1={BOX.padLeft} x2={BOX.width - BOX.padRight} y1={scales.y(tick)} y2={scales.y(tick)} stroke="var(--border)" strokeWidth={1} opacity={0.6} /><text x={BOX.padLeft - 8} y={scales.y(tick)} textAnchor="end" dominantBaseline="middle" className="fill-[var(--muted-foreground)] text-[11px] tabular-nums">{money(tick, sym)}</text></g>);
      $[35] = scales;
      $[36] = sym;
      $[37] = t19;
    } else {
      t19 = $[37];
    }
    let t20;
    if ($[38] !== points || $[39] !== scales) {
      t20 = areaPath(points, scales, BASELINE_Y);
      $[38] = points;
      $[39] = scales;
      $[40] = t20;
    } else {
      t20 = $[40];
    }
    let t21;
    if ($[41] !== t20) {
      t21 = <path d={t20} fill="url(#price-history-fill)" />;
      $[41] = t20;
      $[42] = t21;
    } else {
      t21 = $[42];
    }
    let t22;
    if ($[43] !== points || $[44] !== scales) {
      t22 = linePath(points, scales);
      $[43] = points;
      $[44] = scales;
      $[45] = t22;
    } else {
      t22 = $[45];
    }
    let t23;
    if ($[46] !== t22) {
      t23 = <path d={t22} fill="none" stroke="var(--brand)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />;
      $[46] = t22;
      $[47] = t23;
    } else {
      t23 = $[47];
    }
    let t24;
    if ($[48] !== points || $[49] !== scales) {
      t24 = points.length <= 14 && points.map(p_0 => <circle key={p_0.d} cx={scales.x(p_0.d)} cy={scales.y(p_0.p)} r={2.5} fill="var(--brand)" />);
      $[48] = points;
      $[49] = scales;
      $[50] = t24;
    } else {
      t24 = $[50];
    }
    const t25 = points[Math.floor((points.length - 1) / 2)];
    let t26;
    if ($[51] !== last || $[52] !== points[0] || $[53] !== scales || $[54] !== spansYears || $[55] !== t25) {
      t26 = [points[0], t25, last].filter(_temp3).map((p_2, i_0, a_0) => <text key={p_2.d} x={scales.x(p_2.d)} y={BOX.height - 8} textAnchor={i_0 === 0 ? "start" : i_0 === a_0.length - 1 ? "end" : "middle"} className="fill-[var(--muted-foreground)] text-[11px]">{formatDay(p_2.d, spansYears)}</text>);
      $[51] = last;
      $[52] = points[0];
      $[53] = scales;
      $[54] = spansYears;
      $[55] = t25;
      $[56] = t26;
    } else {
      t26 = $[56];
    }
    let t27;
    if ($[57] !== active || $[58] !== scales) {
      t27 = active && <g><line x1={scales.x(active.d)} x2={scales.x(active.d)} y1={BOX.padTop} y2={BASELINE_Y} stroke="var(--foreground)" strokeWidth={1} opacity={0.25} /><circle cx={scales.x(active.d)} cy={scales.y(active.p)} r={4.5} fill="var(--brand)" stroke="var(--card)" strokeWidth={2} /></g>;
      $[57] = active;
      $[58] = scales;
      $[59] = t27;
    } else {
      t27 = $[59];
    }
    let t28;
    if ($[60] !== onHover || $[61] !== points || $[62] !== scales) {
      t28 = <rect x={BOX.padLeft} y={BOX.padTop} width={BOX.width - BOX.padLeft - BOX.padRight} height={BASELINE_Y - BOX.padTop} fill="transparent" onPointerMove={e => {
        const svg = e.currentTarget.ownerSVGElement;
        if (!svg) {
          return;
        }
        const box = svg.getBoundingClientRect();
        const atX = (e.clientX - box.left) / box.width * BOX.width;
        onHover(nearestIndex(points, scales, atX));
      }} />;
      $[60] = onHover;
      $[61] = points;
      $[62] = scales;
      $[63] = t28;
    } else {
      t28 = $[63];
    }
    let t29;
    if ($[64] !== summary || $[65] !== t17 || $[66] !== t19 || $[67] !== t21 || $[68] !== t23 || $[69] !== t24 || $[70] !== t26 || $[71] !== t27 || $[72] !== t28) {
      t29 = <svg viewBox={`0 0 ${BOX.width} ${BOX.height}`} className="h-56 w-full" preserveAspectRatio="xMidYMid meet" role="img" aria-label={summary} onPointerLeave={t17}>{t18}{t19}{t21}{t23}{t24}{t26}{t27}{t28}</svg>;
      $[64] = summary;
      $[65] = t17;
      $[66] = t19;
      $[67] = t21;
      $[68] = t23;
      $[69] = t24;
      $[70] = t26;
      $[71] = t27;
      $[72] = t28;
      $[73] = t29;
    } else {
      t29 = $[73];
    }
    let t30;
    if ($[74] !== active || $[75] !== scales || $[76] !== spansYears || $[77] !== sym) {
      t30 = active && <div className="pointer-events-none absolute -translate-x-1/2 -translate-y-full rounded-lg bg-foreground px-2.5 py-1.5 text-background shadow-lg" style={{
        left: `${scales.x(active.d) / BOX.width * 100}%`,
        top: `${scales.y(active.p) / BOX.height * 100}%`
      }}><p className="text-xs font-bold tabular-nums leading-none">{money(active.p, sym)}</p><p className="mt-0.5 text-[10px] leading-none opacity-70">{formatDay(active.d, spansYears)}</p></div>;
      $[74] = active;
      $[75] = scales;
      $[76] = spansYears;
      $[77] = sym;
      $[78] = t30;
    } else {
      t30 = $[78];
    }
    if ($[79] !== t29 || $[80] !== t30) {
      t10 = <div className="relative mt-4">{t29}{t30}</div>;
      $[79] = t29;
      $[80] = t30;
      $[81] = t10;
    } else {
      t10 = $[81];
    }
    t8 = "mt-3 flex flex-wrap items-baseline gap-x-5 gap-y-1 border-t border-border/60 pt-3 text-[11px] text-muted-foreground";
    let t31;
    if ($[82] !== last.p || $[83] !== sym) {
      t31 = money(last.p, sym);
      $[82] = last.p;
      $[83] = sym;
      $[84] = t31;
    } else {
      t31 = $[84];
    }
    if ($[85] !== t31) {
      t9 = <span>Now{" "}<span className="font-semibold tabular-nums text-foreground">{t31}</span></span>;
      $[85] = t31;
      $[86] = t9;
    } else {
      t9 = $[86];
    }
    t2 = "Lowest";
    t3 = " ";
    t4 = <span className="font-semibold tabular-nums text-foreground">{money(low.p, sym)}</span>;
    t5 = " ";
    t6 = "on ";
    t7 = formatDay(low.d, spansYears);
    $[2] = active;
    $[3] = last;
    $[4] = onHover;
    $[5] = points;
    $[6] = scales;
    $[7] = sym;
    $[8] = summary;
    $[9] = t10;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    summary = $[8];
    t10 = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
    t6 = $[14];
    t7 = $[15];
    t8 = $[16];
    t9 = $[17];
  }
  let t11;
  if ($[87] !== t2 || $[88] !== t3 || $[89] !== t4 || $[90] !== t5 || $[91] !== t6 || $[92] !== t7) {
    t11 = <span>{t2}{t3}{t4}{t5}{t6}{t7}</span>;
    $[87] = t2;
    $[88] = t3;
    $[89] = t4;
    $[90] = t5;
    $[91] = t6;
    $[92] = t7;
    $[93] = t11;
  } else {
    t11 = $[93];
  }
  let t12;
  if ($[94] !== t11 || $[95] !== t8 || $[96] !== t9) {
    t12 = <div className={t8}>{t9}{t11}</div>;
    $[94] = t11;
    $[95] = t8;
    $[96] = t9;
    $[97] = t12;
  } else {
    t12 = $[97];
  }
  let t13;
  if ($[98] !== summary) {
    t13 = <caption>{summary}</caption>;
    $[98] = summary;
    $[99] = t13;
  } else {
    t13 = $[99];
  }
  let t14;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <thead><tr><th scope="col">Date</th><th scope="col">Lowest price</th></tr></thead>;
    $[100] = t14;
  } else {
    t14 = $[100];
  }
  let t15;
  if ($[101] !== points || $[102] !== sym) {
    let t16;
    if ($[104] !== sym) {
      t16 = p_3 => <tr key={p_3.d}><td>{formatDay(p_3.d, true)}</td><td>{money(p_3.p, sym)}</td></tr>;
      $[104] = sym;
      $[105] = t16;
    } else {
      t16 = $[105];
    }
    t15 = points.map(t16);
    $[101] = points;
    $[102] = sym;
    $[103] = t15;
  } else {
    t15 = $[103];
  }
  let t16;
  if ($[106] !== t15) {
    t16 = <tbody>{t15}</tbody>;
    $[106] = t15;
    $[107] = t16;
  } else {
    t16 = $[107];
  }
  let t17;
  if ($[108] !== t13 || $[109] !== t16) {
    t17 = <table className="sr-only">{t13}{t14}{t16}</table>;
    $[108] = t13;
    $[109] = t16;
    $[110] = t17;
  } else {
    t17 = $[110];
  }
  let t18;
  if ($[111] !== t10 || $[112] !== t12 || $[113] !== t17) {
    t18 = <>{t10}{t12}{t17}</>;
    $[111] = t10;
    $[112] = t12;
    $[113] = t17;
    $[114] = t18;
  } else {
    t18 = $[114];
  }
  return t18;
}
function _temp3(p_1, i, a) {
  return a.indexOf(p_1) === i;
}
function _temp2(min, p) {
  return p.p < min.p ? p : min;
}